type IconType = 'success' | 'error' | 'warning' | 'info' | 'question';

type FireOptions = {
  icon?: IconType;
  title?: string;
  text?: string;
  html?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?: string;
  cancelButtonColor?: string;
  showCancelButton?: boolean;
  allowOutsideClick?: boolean;
  showConfirmButton?: boolean;
  timer?: number;
  timerProgressBar?: boolean;
  width?: string;
  didOpen?: () => void;
  preConfirm?: (...args: any[]) => any;
  [key: string]: any;
};

type FireResult = {
  isConfirmed?: boolean;
  isDenied?: boolean;
  isDismissed?: boolean;
  dismiss?: any;
  value?: any;
};

const TOAST_ROOT_ID = '__vasavi_toast_root__';

function ensureToastRoot() {
  let root = document.getElementById(TOAST_ROOT_ID);
  if (root) return root;
  root = document.createElement('div');
  root.id = TOAST_ROOT_ID;
  root.style.position = 'fixed';
  root.style.top = '14px';
  root.style.right = '14px';
  root.style.zIndex = '99999';
  root.style.display = 'flex';
  root.style.flexDirection = 'column';
  root.style.gap = '10px';
  root.style.alignItems = 'flex-end';
  root.style.pointerEvents = 'none';
  document.body.appendChild(root);
  return root;
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function showToast(icon: IconType | undefined, title: string | undefined, text: string | undefined) {
  const root = ensureToastRoot();
  const card = document.createElement('div');
  card.style.pointerEvents = 'auto';
  card.style.width = 'min(380px, calc(100vw - 28px))';
  card.style.display = 'grid';
  card.style.gridTemplateColumns = '36px 1fr 28px';
  card.style.gap = '10px';
  card.style.alignItems = 'start';
  card.style.padding = '12px 12px';
  card.style.borderRadius = '16px';
  card.style.border = '1px solid rgba(229,231,235,0.9)';
  card.style.background = 'rgba(255,255,255,0.96)';
  card.style.boxShadow = '0 18px 45px rgba(15,23,42,0.14)';
  card.style.backdropFilter = 'blur(10px)';
  card.style.position = 'relative';
  card.style.overflow = 'hidden';
  card.style.fontFamily = `"Inter", system-ui, -apple-system, "Segoe UI", sans-serif`;

  const iconWrap = document.createElement('div');
  iconWrap.style.width = '32px';
  iconWrap.style.height = '32px';
  iconWrap.style.borderRadius = '999px';
  iconWrap.style.display = 'grid';
  iconWrap.style.placeItems = 'center';
  iconWrap.style.fontWeight = '800';
  iconWrap.style.color = '#111827';
  iconWrap.style.background = '#FFFBEB';
  iconWrap.style.border = '1px solid rgba(212,175,55,0.5)';

  const symbol = document.createElement('span');
  symbol.textContent =
    icon === 'success' ? '✓' :
    icon === 'error' ? '!' :
    icon === 'warning' ? '⚠' :
    icon === 'info' ? 'i' : '•';
  iconWrap.appendChild(symbol);

  const body = document.createElement('div');
  const tTitle = document.createElement('div');
  if (title) {
    tTitle.textContent = title;
    tTitle.style.fontSize = '12px';
    tTitle.style.fontWeight = '750';
    tTitle.style.letterSpacing = '0.04em';
    tTitle.style.textTransform = 'uppercase';
    tTitle.style.marginBottom = '3px';
    tTitle.style.color = '#7a5a11';
    body.appendChild(tTitle);
  }
  const msg = document.createElement('div');
  msg.innerHTML = text ? escapeHtml(text) : '';
  msg.style.fontSize = '13px';
  msg.style.lineHeight = '1.45';
  msg.style.color = '#111827';
  body.appendChild(msg);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.textContent = '×';
  closeBtn.style.width = '28px';
  closeBtn.style.height = '28px';
  closeBtn.style.borderRadius = '10px';
  closeBtn.style.border = '1px solid rgba(148,163,184,0.4)';
  closeBtn.style.background = 'rgba(255,255,255,0.9)';
  closeBtn.style.color = '#334155';
  closeBtn.style.cursor = 'pointer';
  closeBtn.style.fontSize = '18px';
  closeBtn.style.lineHeight = '1';
  closeBtn.style.display = 'grid';
  closeBtn.style.placeItems = 'center';
  closeBtn.onclick = () => card.remove();

  const bar = document.createElement('div');
  bar.style.position = 'absolute';
  bar.style.left = '0';
  bar.style.bottom = '0';
  bar.style.height = '3px';
  bar.style.width = '100%';
  bar.style.background = 'linear-gradient(90deg,#d4af37,#b68d1f,#ead48a)';
  bar.style.transformOrigin = 'left';
  bar.style.animation = 'vasaviToastBar 3.8s linear forwards';

  // inject keyframes once
  const styleId = '__vasavi_toast_style__';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
@keyframes vasaviToastBar { from { transform: scaleX(1);} to { transform: scaleX(0);} }
@keyframes vasaviToastIn { from { opacity:0; transform: translateY(-6px);} to { opacity:1; transform: translateY(0);} }
`;
    document.head.appendChild(style);
  }
  card.style.animation = 'vasaviToastIn 160ms ease-out';

  card.appendChild(iconWrap);
  card.appendChild(body);
  card.appendChild(closeBtn);
  card.appendChild(bar);

  root.appendChild(card);
  window.setTimeout(() => {
    if (card.isConnected) card.remove();
  }, 3800);
}

let loadingCount = 0;

const Swal = {
  DismissReason: { cancel: 'cancel' },

  fire: async (opts: FireOptions | string, maybeText?: string, maybeIcon?: IconType): Promise<FireResult> => {
    // Support Swal.fire('title','text','success')
    if (typeof opts === 'string') {
      showToast(maybeIcon, opts, maybeText);
      return { isConfirmed: true };
    }

    // "loading" style calls
    if (opts?.didOpen) {
      try { opts.didOpen(); } catch {}
    }

    const icon = opts.icon;
    const title = opts.title;
    const text = opts.text ?? (opts.html ? opts.html.replace(/<[^>]*>/g, '') : undefined);

    // Confirm dialogs -> window.confirm, but still keep toaster look for message
    if (opts.showCancelButton) {
      const ok = window.confirm(`${title ? title + '\n\n' : ''}${text || ''}`.trim());
      return ok ? { isConfirmed: true } : { isDismissed: true, dismiss: Swal.DismissReason.cancel };
    }

    // Default: show toast message
    showToast(icon, title, text);

    // timer handling
    if (opts.timer && opts.timer > 0) {
      // already auto-dismissed by toast
    }
    return { isConfirmed: true };
  },

  showLoading: () => {
    loadingCount += 1;
  },

  close: () => {
    loadingCount = Math.max(0, loadingCount - 1);
  },

  update: (_opts: any) => {
    // no-op for toast shim
  },

  showValidationMessage: (message: string) => {
    showToast('error', 'Validation', message);
  }
};

export default Swal;

